import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useTrialDetails from '../../../../../containers/team/hooks/useTrialDetails';
import useAnalyticsTracker from '../../../../../analytics/hooks/useAnalyticsTracker';
import { PipelineListTrackers } from '../../../tracking/pipeline-list-trackers';

const SCHEDULE_DEMO_POPUP_SHOWN_KEY = 'schedule_demo_popup_shown';

const ALLOWED_PATH_PATTERNS = [
  /^\/$/,
  /^\/pipeline$/,
  /^\/pipeline\/create(\/|$)/,
  /^\/pipeline\/edge\/\d+\/create(\/|$)/
];

function isAllowedPath(pathname: string): boolean {
  return ALLOWED_PATH_PATTERNS.some(pattern => pattern.test(pathname));
}

function isAlreadyShown(): boolean {
  try {
    return !!localStorage.getItem(SCHEDULE_DEMO_POPUP_SHOWN_KEY);
  } catch {
    return true;
  }
}

function markAsShown(): void {
  try {
    localStorage.setItem(SCHEDULE_DEMO_POPUP_SHOWN_KEY, 'true');
  } catch {
    // localStorage unavailable
  }
}

export function useExitIntent() {
  const [showModal, setShowModal] = useState(false);
  const listenerAttached = useRef(false);
  const triggeredRef = useRef(false);
  const { isTrialUser } = useTrialDetails();
  const { eventTrack } = useAnalyticsTracker();
  const location = useLocation();

  const isOnAllowedPage = isAllowedPath(location.pathname);

  const triggerPopup = useCallback(() => {
    if (triggeredRef.current || !isAllowedPath(window.location.pathname) || isAlreadyShown()) {
      return;
    }
    triggeredRef.current = true;
    setShowModal(true);
    eventTrack({
      action: PipelineListTrackers.PIPELINE_TRACKER_SCHEDULE_DEMO_POPUP_SHOWN
    });
  }, []);

  useEffect(() => {
    if (!isTrialUser || !isOnAllowedPage || listenerAttached.current) {
      return;
    }

    if (isAlreadyShown()) {
      return;
    }

    listenerAttached.current = true;

    const handleMouseLeave = () => {
      triggerPopup();
      document.documentElement.removeEventListener('mouseleave', handleMouseLeave);
    };

    document.documentElement.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.documentElement.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isTrialUser, isOnAllowedPage]);

  const dismissModal = useCallback(() => {
    setShowModal(false);
    markAsShown();
    eventTrack({
      action: PipelineListTrackers.PIPELINE_TRACKER_SCHEDULE_DEMO_POPUP_DISMISSED
    });
  }, []);

  return { showModal, dismissModal };
}
